import React, { useEffect, useState } from 'react'
import { Collapse } from 'react-collapse'
import { Col, Row } from 'react-styled-flexboxgrid'

import { AcornsIcon } from '@acorns/icons'
import { Bodytext, Button, Card } from '@acorns/web-components'
import styled, { css } from 'styled-components'

import { Heading2, Subheading, Title } from 'components/typography'
import { ProductKey, TierProduct } from 'generated/types'
import { style } from 'theme'

import { TierProps } from './index'
import { PRODUCTS } from './tier-card-constants'

const ChevronUp = styled(AcornsIcon)`
  display: block;
  margin: 0 auto;
  width: 10px;
  margin-top: 6px;
`
const ChevronDown = styled(ChevronUp)`
  transform: rotate(180deg);
  display: block;
  margin: 0 auto;
  margin-top: 6px;
`

type Handlers = {
  handleChangeTierSelection: (tierId: string, tierKey: string) => void
}

type OuterProps = {
  tierId: string
  nextBillingDate: string | null
  currentTierInfo: {
    id: string
    key: string
    includedProducts: TierProduct[]
  }
  isDowngradableTier: boolean
  setCurrentTierShowing: (currentTierShowing: boolean) => void
  ref?: any
}

type Item = {
  product?: ProductKey
  name?: string
  slogan?: string
  icon?: JSX.Element
  description?: string[]
}

type Props = Partial<TierProps> & OuterProps & Handlers & Item

const AccessibilityIds = {
  listItem: 'tier-description-list-item',
}

const StyledCard = styled<any>(Card)`
  border-radius: 10px !important;
  ${(props) =>
    props.currentPlan &&
    css`
      border: 1.5px solid ${style((theme) => theme.colors.green)} !important;
    `};
  box-shadow: ${style((theme) => theme.shadows.lgMedium)} !important;
  min-height: 589px;
  margin: 0 auto;
  position: relative;
  padding-left: 0px !important;
  padding-right: 0px !important;
  @media screen and (max-width: 1024px) {
    min-height: 580px;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.md - 1,
    )}px) {
    width: 100%;
    min-height: 280px;
    margin-top: 20px;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
    min-height: 350px;
    margin-top: 20px;
  }
`

const TierTitle = styled(Title)`
  font-size: 16px;
  font-weight: 600;
  height: 24px;
  line-height: 24px;
  text-align: center;
`

const SubTitle = styled(Heading2)`
  font-size: 14px;
  font-weight: 600;
  height: 22px;
  letter-spacing: 0px;
  line-height: 22px;
  width: 100%;
  color: ${style((theme) => theme.colors.green)};
  text-align: center;
  margin-bottom: 20px;
`

const Badge = styled.div`
  background: ${style((theme) => theme.colors.sky)};
  border-radius: 22px;
  height: 30px;
  width: 112px;
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  margin-bottom: 20px;
`

const Label = styled(Subheading)`
  line-height: 16px;
  height: 16px;
  font-size: 12px;
  color: ${style((theme) => theme.colors.ivory)};
  letter-spacing: 0px;
`

const StyledIcon = styled.div`
  height: 24px;
  width: 24px;
  object-fit: contain;
  vertical-align: middle;
  display: inline-block;
`

const StyledPlusIcon = styled(StyledIcon)`
  float: right;
`
const SloganList = styled.ul`
  margin-bottom: 0px;
  list-style-type: none;
  padding-left: 0px;
  margin-top: 0px;
  width: 100%;
`

const DescriptionList = styled.ul`
  list-style-type: disc;
  margin-top: 0px;
  width: 100%;
  color: ${style((theme) => theme.colors.stone)};
  margin-bottom: 0px;
  list-style-position: inside;
  text-indent: -1em;
  padding-left: 1em;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: inherit;
  padding: 40px 0;
  height: auto;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.md - 1,
    )}px) {
    padding: 40px 0;
    width: 100%;
  }
  @media screen and (max-width: ${style((theme) => theme.breakpoints.sm)}px) {
    padding: 30px 0;
  }
  @media screen and (max-width: 320px) {
    padding: 20px 0;
  }
`
const CheckContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  height: 14px;
  width: 14px;
  min-height: 14px;
  min-width: 14px;
  margin-right: 4px;
`

const SloganText = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
  font-size: 14px;
  height: 50px;
  color: ${style((theme) => theme.colors.slate)};
`
const WrappingDiv = styled.div`
  display: inline-block;
`
const Trigger = styled.div`
  text-align: left;
  background-color: transparent;
  border-width: 0;
  cursor: pointer;
  outline: none;
`

const StyledText = styled.span`
  height: 24px;
  line-height: 24px;
  margin-left: 10px;
  display: inline-block;
  vertical-align: middle;
`

const DescriptionItem = styled.li`
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 22px;
  max-width: 100%;
  padding-bottom: 10px;
  padding-left: 20px;
`

const StyledButton = styled(Button)`
  width: 100%;
  margin: 0 auto;
  @media screen and (max-width: 890px) and (min-width: ${style(
      (theme) => theme.breakpoints.sm,
    )}px) {
    width: 50%;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: 30px;
  }
`

const TierRow = styled(Row)`
  width: 100%;
  align-self: flex-start;
  margin-left: 0;
  margin-right: 0;
`

const ButtonContainer = styled.div`
  margin-top: auto;
  margin-left: 0;
  margin-right: 0;
  padding: 20px 0;
`

const HighlightContainer = styled.div`
  background-color: ${style((theme) => theme.colors.ivoryLight)};
  color: ${style((theme) => theme.colors.stone)};
  padding: 16px;
  width: 100%;
  font-size: 12px;
  text-align: center;
  border-radius: ${style((theme) => theme.styles.smallBorderRadius)};
  & p {
    margin: 0;
  }
`

const StyledCol = styled(Col)`
  padding: 0px;
`
const PaddingWrapper = styled.div`
  padding: 0 40px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.md - 1,
    )}px) {
    padding: 0 40px;
    width: 100%;
  }
  @media screen and (max-width: ${style((theme) => theme.breakpoints.sm)}px) {
    padding: 0 30px;
  }
  @media screen and (max-width: 320px) {
    padding: 0 20px;
  }
`
const PaddingWrapperTop = styled(PaddingWrapper)`
  border-bottom: 1px solid ${style((theme) => theme.colors.ivory)};
`
const PaddingWrapperProducts = styled(PaddingWrapper)`
  padding-top: 20px;
`

const StyledBodyText = styled(Bodytext)`
  text-align: center;
  margin-bottom: 20px;
`
const renderDescriptions = (
  descriptions: Item['description'],
  key: Item['product'],
  _: string,
  openCollapse: boolean,
) => {
  return descriptions?.map((item, index) => {
    return (
      <Collapse key={`${index}_${key}`} isOpened={openCollapse}>
        <DescriptionItem id={AccessibilityIds.listItem}>{item}</DescriptionItem>
      </Collapse>
    )
  })
}

const Product = ({ item, tierId }: { item: Item; tierId: string }) => {
  const [openCollapse, setOpenCollapse] = useState(false)

  const handleProductToggleSelect = () => {
    setOpenCollapse(!openCollapse)
  }
  return (
    <>
      <Trigger
        onClick={handleProductToggleSelect}
        role="button"
        aria-controls={AccessibilityIds.listItem}
        aria-expanded={openCollapse}
      >
        <SloganText key={item.product}>
          <WrappingDiv>
            <StyledIcon>{item.icon}</StyledIcon>
            <StyledText>
              <b>{item.name}</b> {item.slogan}
            </StyledText>
          </WrappingDiv>
          <StyledPlusIcon>
            {openCollapse ? (
              <ChevronUp icon={AcornsIcon.Icon.UpChevron} />
            ) : (
              <ChevronDown icon={AcornsIcon.Icon.UpChevron} />
            )}
          </StyledPlusIcon>
        </SloganText>
      </Trigger>

      <DescriptionList>
        {renderDescriptions(
          item.description,
          item.product,
          tierId,
          openCollapse,
        )}
      </DescriptionList>
    </>
  )
}

// disble linter as it is not happy with useEffect here
const TierCard: React.SFC<
  Omit<Props, 'product' | 'description' | 'slogans' | 'icon' | 'slogan'>
> = (props: Props) => {
  const slogansToUse = PRODUCTS

  useEffect(() => {
    if (props.currentPlan) {
      props.setCurrentTierShowing(true)
    }
  }, [])
  return (
    <StyledCard
      ref={props.ref}
      currentPlan={props.currentPlan}
      product={props.product}
      data-testid={props.tier?.key + '_Card'}
    >
      <Container>
        <PaddingWrapperTop>
          <TierRow>
            <StyledCol xs={12}>
              <TierTitle>{props.tier?.name}</TierTitle>
              <SubTitle>{`$${props.cost} per month`}</SubTitle>
              <StyledBodyText
                data-testid={props.tier?.key + '_select_tier_body'}
                key={props.name}
              >
                {props.tier?.description}
              </StyledBodyText>
              {props.currentPlan && (
                <Badge>
                  <CheckContainer>
                    <AcornsIcon
                      icon={AcornsIcon.Icon.TransparentCheckmark}
                      width="14px"
                    />
                  </CheckContainer>
                  <Label>Current plan</Label>
                </Badge>
              )}
            </StyledCol>
          </TierRow>
        </PaddingWrapperTop>
        <PaddingWrapperProducts>
          <TierRow>
            {props.tier?.includedProducts?.map((product) => (
              <SloganList key={product.key}>
                {slogansToUse.map((item) => {
                  if (item.product === product.key) {
                    return <Product item={item} tierId={props.tierId} />
                  }
                  return null
                })}
              </SloganList>
            ))}
          </TierRow>
        </PaddingWrapperProducts>
        {!props.currentPlan && (
          <ButtonContainer>
            <PaddingWrapper>
              <StyledButton
                data-testid={props.tier?.key + '_select_tier_cta'}
                onPress={() =>
                  props.handleChangeTierSelection(
                    props.tierId,
                    props.tier?.key ?? '',
                  )
                }
                secondary={props.isDowngradableTier ? true : false}
              >
                {props.isDowngradableTier
                  ? 'Switch to this plan'
                  : 'Upgrade to this plan'}
              </StyledButton>
            </PaddingWrapper>
          </ButtonContainer>
        )}

        {props.currentPlan && props.nextBillingDate && (
          <ButtonContainer>
            <PaddingWrapper>
              <HighlightContainer>
                <p>Next billing date: {props.nextBillingDate}</p>
              </HighlightContainer>
            </PaddingWrapper>
          </ButtonContainer>
        )}
      </Container>
    </StyledCard>
  )
}

export default TierCard
